import { Workbox } from "workbox-window";

import { isCacheEnabled } from "api/serviceWorker/common";

/**
 * Регистрирует SW: в проде Workbox (кэш + FCM), в dev — только Firebase Messaging.
 * @param onWaitingUpdate — пользователь подтвердил обновление; нужно вызвать messageSkipWaiting
 */
export async function registerServiceWorker(onWaitingUpdate: (wb: Workbox) => void) {
    if (isCacheEnabled()) {
        await unregisterLegacyFirebaseServiceWorker();
        registerCacheServiceWorker(onWaitingUpdate);
        return;
    }
    registerFirebaseServiceWorker();
}

/**
 * Снимает все регистрации SW (включая устаревший firebase-messaging-sw.js).
 */
export function unregisterAllServiceWorkers(onUnregister: () => void) {
    if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
            const unregisterPromises = registrations.map((registration) =>
                registration.unregister()
            );
            Promise.all(unregisterPromises).then(() => {
                onUnregister();
            });
        });
    }
}

/**
 * Находит регистрацию основного SW (Workbox) или fallback FCM в dev.
 */
export async function getAppServiceWorkerRegistration(): Promise<
    ServiceWorkerRegistration | undefined
> {
    if (!("serviceWorker" in navigator)) {
        return undefined;
    }
    const registrations = await navigator.serviceWorker.getRegistrations();
    const byScript = (suffix: string) =>
        registrations.find((registration) => {
            const scriptURL =
                registration.active?.scriptURL ||
                registration.waiting?.scriptURL ||
                registration.installing?.scriptURL ||
                "";
            return scriptURL.endsWith(suffix);
        });
    return byScript("/service-worker.js") || byScript("/firebase-messaging-sw.js");
}

/**
 * Снимает старый FCM SW с scope `/`, чтобы не вытеснять Workbox.
 */
async function unregisterLegacyFirebaseServiceWorker() {
    if (!("serviceWorker" in navigator)) {
        return;
    }
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
        registrations
            .filter((registration) => {
                const scriptURL =
                    registration.active?.scriptURL ||
                    registration.waiting?.scriptURL ||
                    registration.installing?.scriptURL ||
                    "";
                return scriptURL.endsWith("/firebase-messaging-sw.js");
            })
            .map((registration) => registration.unregister())
    );
}

function registerCacheServiceWorker(onWaitingUpdate: (wb: Workbox) => void) {
    if (!("serviceWorker" in navigator)) {
        return;
    }
    try {
        const wb = new Workbox("/service-worker.js");

        wb.addEventListener("waiting", (event) => {
            if (event.isUpdate) {
                onWaitingUpdate(wb);
                return;
            }
            wb.messageSkipWaiting();
        });

        wb.addEventListener("controlling", (event) => {
            if (event.isUpdate) {
                window.location.reload();
            }
        });

        wb.register().catch((error) => {
            if (error.message && error.message.includes("MIME")) {
                console.error("MIME type problem - check /service-worker.js");
            }
        });
    } catch (error) {
        console.error("Workbox create error:", error);
    }
}

function registerFirebaseServiceWorker() {
    if ("serviceWorker" in navigator) {
        navigator.serviceWorker.register("/firebase-messaging-sw.js").catch(function (err) {
            console.error("Firebase Service Worker registration failed:", err);
        });
    }
}
